/* eslint-disable max-classes-per-file -- It's better to keep InternalCurrentRequestStore co-located */
import { AsyncLocalStorage } from 'node:async_hooks';
import { v4 as uuidv4 } from 'uuid';

import type { Nullable } from '@/type-utils';
import { InvalidStateError, ServerCodeInClientError } from '@/utils/errors';
import { waitUntil } from '@vercel/functions';
import type {
  AnyRoute,
  NormalizedRequestModel,
  NormalizedResponseModel,
  RouteType
} from '../../models/Http';
import { RequestCacheStore } from './RequestCacheStore';

/**
 * Used to obtain the current request context on the server-side. While this is
 * intended to only be practically used on the server-side, it is an isomorphic
 * service because other isomorphic services may use this and handle any returned
 * `null` values.
 */
class CurrentRequestService {
  private _asyncLocalStorage: Nullable<
    AsyncLocalStorage<InternalCurrentRequestStore>
  >;

  private _appInstanceID: Nullable<string>;

  /** @returns The existing or a new instance of `AsyncLocalStorage`. */
  private get asyncLocalStorage(): AsyncLocalStorage<InternalCurrentRequestStore> {
    // Not using the `EnvironmentService` here as it would create a circular
    // dependency. Uses Webpack's dead branch detection to always throw on
    // the client, since no code on the client should be attempting to use Node APIs,
    // if we correctly handled client detection elsewhere in this file..
    if (typeof window !== 'undefined') {
      throw new ServerCodeInClientError(
        `There was an attempt to use \`CurrentRequestService\`'s \`AsyncLocalStorage\` ` +
          `in the client. This indicates server code leaking into the client. All public ` +
          `properties and methods on \`CurrentRequestService\` should preemptively detect` +
          ` whether the code is executing on the client prior to accessing this property.`
      );
    }

    this._asyncLocalStorage ??= new AsyncLocalStorage();
    return this._asyncLocalStorage;
  }

  /**
   * The application instance ID, used to identify the specific serverless function being
   * used to make a call. In deployments, Next runs in a serverless environment and each
   * request is potentially executed in a new lambda that has no state in memory from
   * previous requests.
   *
   * This is unlike a traditional web server where each request is executed in the same
   * process. When a request is being made with a "warm" lambda, the `appInstanceID` will
   * be the same for all requests made within that same lambda. When a request is being
   * handled with a "cold" lambda, the `appInstanceID` will be different from previous
   * requests.
   *
   * @returns A unique identifier for this instantiation of the the request service.
   * @see https://docs.aws.amazon.com/lambda/latest/dg/runtimes-context.html
   * @see https://vercel.com/docs/concepts/functions/serverless-functions
   */
  public get appInstanceID(): string {
    this._appInstanceID ??= uuidv4();
    return this._appInstanceID;
  }

  /**
   * `inRequest` is used to determine if the current code is excuting with the
   * context of a request. **IMPORTANT:** This is _not_ a replacement for
   * `EnvironmentService.onServer` as that value is known at compile time and
   * this value can only be known at runtime. To allow dead server code to be
   * eliminated on the client, this should always be paired with a check to see
   * if we're executing on the server first - either together or in an outer
   * closure.
   * @returns `true` if the current scope is executing within the context of a
   * request.
   * @example ```ts
   * if (EnvironmentService.onServer && CurrentRequest.inRequest) {
   *  // The code is running on the server within a request.
   * }
   * ```
   */
  public get inRequest(): boolean {
    // Not using the `EnvironmentService` here as it would create a circular
    // dependency. Uses Webpack's dead branch detection to always return `false` on
    // the client.
    if (typeof window !== 'undefined') return false;
    return this.asyncLocalStorage.getStore() !== undefined;
  }

  /**
   * @returns The cache for storing computed values based on request keys, per request.
   */
  public get cache(): RequestCacheStore {
    if (typeof window !== 'undefined') {
      throw new ServerCodeInClientError(
        `There was an attempt to use \`CurrentRequestService\`'s \`cache\` ` +
          `in the client. This indicates server code leaking into the client. All public ` +
          `properties and methods on \`CurrentRequestService\` should preemptively detect` +
          ` whether the code is executing on the client prior to accessing this property.`
      );
    }

    const store = this.asyncLocalStorage.getStore();
    if (!store) {
      throw new InvalidStateError(
        `Could not get current request. Did you forget to wrap a route in \`middleware\`?`
      );
    }
    return store.requestCache;
  }

  /**
   * Extends the lifetime of the request handler at least until the given promise resolves.
   * @param promise - The promise to wait for.
   * @throws A {@link ServerCodeInClientError} if called from the client.
   */
  public waitUntil(promise: Promise<unknown>): void {
    if (typeof window !== 'undefined') {
      throw new ServerCodeInClientError(
        `There was an attempt to use \`CurrentRequestService\`'s \`waitUntil\` ` +
          `in the client. This indicates server code leaking into the client. All public ` +
          `properties and methods on \`CurrentRequestService\` should preemptively detect` +
          ` whether the code is executing on the client prior to accessing this property.`
      );
    }

    waitUntil(promise);
  }

  /**
   * Returns the current request/response in scope as a tuple.
   *
   * @returns The `NormalizedRequestModel`/`NormalizedResponseModel` tuple for the current request in scope.
   * @throws `InvalidStateError` if we're on the server and the request object cannot
   * be retrieved.
   * @throws `ServerCodeInClientError` if we're on the client.
   */
  public get<T extends RouteType = AnyRoute>(): [
    NormalizedRequestModel<T>,
    NormalizedResponseModel<T>
  ] {
    // Not using the `EnvironmentService` here as it would create a circular
    // dependency.
    if (typeof window !== 'undefined') {
      throw new ServerCodeInClientError(
        'CurrentRequestService.get() called on the client'
      );
    }

    const store = this.asyncLocalStorage.getStore();

    if (!store) {
      throw new InvalidStateError(
        `Could not get current request. Did you forget to wrap a route in \`middleware\`?`
      );
    }

    const { request } = store;

    return [request, request.response];
  }

  /**
   * Returns the current request/response in scope as a tuple.
   *
   * @returns The `NormalizedRequestModel`/`NormalizedResponseModel` tuple for the current
   * request in scope or `null` if we're unable to retrieve the current request object.
   */
  public tryGet<T extends RouteType = AnyRoute>():
    | [null, null]
    | [NormalizedRequestModel<T>, NormalizedResponseModel<T>] {
    // Try to get the current request/response
    try {
      // If not on app router try to get the request from the async local storage.
      return this.get<T>();
    } catch (e) {
      // Would be nice to log this error but can't use
      // `LoggerService` here :(
      return [null, null];
    }
  }

  /**
   * Wraps a function so that it may call `CurrentRequest.get()` to obtain
   * information about the current request.
   * @param request - The `IMiddlewareRequest` request object to represent this
   * request in the current scope.
   * @param callback - The function to call to which this request will belong.
   * @returns The return value of the provided function.
   * @throws `ServerCodeInClientError` when the method is called from within the
   * browser.
   */
  // eslint-disable-next-line @eslint-react/hooks-extra/no-unnecessary-use-prefix -- This is not a hook.
  public async use<T extends NormalizedRequestModel, U>(
    request: T,
    callback: () => Promise<U>
  ): Promise<U> {
    // Not using the `EnvironmentService` here as it would create a circular dependency.
    // Throw an error if there is an attempt to call this method on the client.
    if (typeof window !== 'undefined') {
      throw new ServerCodeInClientError(
        'There was an attempt to call `CurrentRequest.use` in the browser. ' +
          'This method may only be used server-side as it is intended for server-side request ' +
          'handling and uses Node JS APIs.'
      );
    }

    return this.asyncLocalStorage.run(
      new InternalCurrentRequestStore(request),
      callback
    );
  }
}

/** An internal store for managing the current request. */
class InternalCurrentRequestStore {
  private _requestCache: RequestCacheStore | undefined;

  /** @returns The request cache for the current request. */
  public get requestCache(): RequestCacheStore {
    this._requestCache ??= new RequestCacheStore();
    return this._requestCache;
  }

  /**
   * Creates a current request store.
   * @param request - The normalized request model.
   */
  public constructor(public readonly request: NormalizedRequestModel) {}
}

export default new CurrentRequestService();
